/**
 * Form validation.
 *
 * The legacy app validated inline, inside submit handlers, in Arabic, with the
 * rules repeated per form. These are the same rules — including the Gmail-only
 * requirement the patient signup enforced — as pure functions returning a field
 * map, so both the UI and the test suite can exercise them.
 *
 * Every validator returns `{ valid, errors }` where `errors` is
 * `{ [field]: 'arabic message' }`; an empty object means the form is clean.
 */

const required = (value) => String(value ?? '').trim().length > 0;

export const MESSAGES = {
  required: 'هذا الحقل مطلوب',
  usernameShort: 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل',
  passwordShort: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل',
  passwordMismatch: 'كلمتا المرور غير متطابقتين',
  emailInvalid: 'يرجى إدخال بريد إلكتروني صالح',
  gmailOnly: 'يجب استخدام بريد إلكتروني صالح من جوجل (Gmail) للتسجيل.',
  phoneInvalid: 'رقم الهاتف غير صالح (يجب أن يبدأ بـ 01 ويتكون من 11 رقم)',
  priceInvalid: 'السعر يجب أن يكون رقماً موجباً',
  percentInvalid: 'النسبة يجب أن تكون بين 0 و 100',
  timeOrder: 'وقت النهاية يجب أن يكون بعد وقت البداية',
  slotDuration: 'مدة الكشف يجب أن تكون أكبر من صفر',
  noWorkingDays: 'يجب اختيار يوم عمل واحد على الأقل',
  dateRequired: 'يرجى اختيار التاريخ',
  slotRequired: 'يرجى اختيار الموعد',
  depositReceiptRequired:
    'يجب رفع صورة إيصال التحويل أولاً — الحجز يتطلب دفع عربون ولن يتم تأكيده بدون إيصال صحيح.',
};

/* ==========================================================================
   Primitives
   ========================================================================== */

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

/** The signup form accepted Gmail addresses only — kept, since Google auth depends on it. */
export const isGmail = (email) =>
  isValidEmail(email) && String(email).trim().toLowerCase().endsWith('@gmail.com');

/** Egyptian mobile format: 11 digits starting 01. */
export const isValidEgyptianPhone = (phone) => /^01\d{9}$/.test(String(phone || '').trim());

export const isPositiveNumber = (value) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) && n >= 0;
};

export const isPercent = (value) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) && n >= 0 && n <= 100;
};

/* ==========================================================================
   Forms
   ========================================================================== */

export function validateLogin({ username, password }) {
  const errors = {};
  if (!required(username)) errors.username = MESSAGES.required;
  if (!required(password)) errors.password = MESSAGES.required;
  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Patient signup — mirrors `handlePatientAuth`'s signup branch.
 *
 * `rules` (المرحلة 1.1) carries the platform's policies when they differ from
 * what the form always enforced: `emailAny` when `emailDomain` accepts any
 * address, `phoneOk` when the country is not Egypt. Left out, the rules are
 * the old ones exactly.
 */
export function validatePatientSignup({ name, username, email, phone, password }, rules = {}) {
  const { emailAny = false, phoneOk = isValidEgyptianPhone } = rules;
  const errors = {};
  if (!required(name)) errors.name = MESSAGES.required;
  if (!required(username)) errors.username = MESSAGES.required;
  else if (String(username).trim().length < 3) errors.username = MESSAGES.usernameShort;
  if (!required(email)) errors.email = MESSAGES.required;
  else if (emailAny ? !isValidEmail(email) : !isGmail(email)) errors.email = emailAny ? MESSAGES.emailInvalid : MESSAGES.gmailOnly;
  if (required(phone) && !phoneOk(phone)) errors.phone = MESSAGES.phoneInvalid;
  if (!required(password)) errors.password = MESSAGES.required;
  else if (String(password).length < 6) errors.password = MESSAGES.passwordShort;
  return { valid: Object.keys(errors).length === 0, errors };
}

/** Doctor registration request — mirrors `handleDoctorRegistration`. */
export function validateDoctorRegistration(
  { name, username, password, email, phone, categoryId, priceExam, priceFollowup },
  rules = {},
) {
  const { phoneOk = isValidEgyptianPhone } = rules;
  const errors = {};
  if (!required(name)) errors.name = MESSAGES.required;
  if (!required(username)) errors.username = MESSAGES.required;
  else if (String(username).trim().length < 3) errors.username = MESSAGES.usernameShort;
  if (!required(password)) errors.password = MESSAGES.required;
  else if (String(password).length < 6) errors.password = MESSAGES.passwordShort;
  if (required(email) && !isValidEmail(email)) errors.email = MESSAGES.emailInvalid;
  if (required(phone) && !phoneOk(phone)) errors.phone = MESSAGES.phoneInvalid;
  if (!required(categoryId)) errors.categoryId = MESSAGES.required;
  if (priceExam !== undefined && !isPositiveNumber(priceExam)) errors.priceExam = MESSAGES.priceInvalid;
  if (priceFollowup !== undefined && !isPositiveNumber(priceFollowup)) {
    errors.priceFollowup = MESSAGES.priceInvalid;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validatePasswordChange({ newPassword, confirmPassword }) {
  const errors = {};
  if (!required(newPassword)) errors.newPassword = MESSAGES.required;
  else if (String(newPassword).length < 6) errors.newPassword = MESSAGES.passwordShort;
  if (newPassword !== confirmPassword) errors.confirmPassword = MESSAGES.passwordMismatch;
  return { valid: Object.keys(errors).length === 0, errors };
}

/** Clinic editor — the working-window rules `handleDoctorSaveClinic` enforced. */
export function validateClinic(clinic = {}) {
  const errors = {};
  if (!required(clinic.name)) errors.name = MESSAGES.required;

  const usesWindow = clinic.startTime || clinic.endTime || clinic.duration;
  if (usesWindow) {
    if (!required(clinic.startTime)) errors.startTime = MESSAGES.required;
    if (!required(clinic.endTime)) errors.endTime = MESSAGES.required;
    if (clinic.startTime && clinic.endTime && clinic.endTime <= clinic.startTime) {
      errors.endTime = MESSAGES.timeOrder;
    }
    if (!Number(clinic.duration) || Number(clinic.duration) <= 0) {
      errors.duration = MESSAGES.slotDuration;
    }
  }

  if (!Array.isArray(clinic.workingDays) || clinic.workingDays.length === 0) {
    errors.workingDays = MESSAGES.noWorkingDays;
  }

  if (clinic.examination_price !== undefined && !isPositiveNumber(clinic.examination_price)) {
    errors.examination_price = MESSAGES.priceInvalid;
  }
  if (clinic.followup_price !== undefined && !isPositiveNumber(clinic.followup_price)) {
    errors.followup_price = MESSAGES.priceInvalid;
  }

  const depositOn = clinic.requireDeposit === true || clinic.requireDeposit === 'true';
  if (depositOn) {
    if (
      clinic.examination_deposit_percentage !== undefined &&
      !isPercent(clinic.examination_deposit_percentage)
    ) {
      errors.examination_deposit_percentage = MESSAGES.percentInvalid;
    }
    if (
      clinic.followup_deposit_percentage !== undefined &&
      !isPercent(clinic.followup_deposit_percentage)
    ) {
      errors.followup_deposit_percentage = MESSAGES.percentInvalid;
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Booking submission. `depositRequired` comes from the pricing quote, and its
 * receipt rule is the one hard block the legacy submit handler applied.
 */
export function validateBookingSubmission({ dateKey, time, depositRequired, depositReceipt }) {
  const errors = {};
  if (!required(dateKey)) errors.date = MESSAGES.dateRequired;
  if (!required(time)) errors.time = MESSAGES.slotRequired;
  if (depositRequired && !depositReceipt) errors.depositReceipt = MESSAGES.depositReceiptRequired;
  return { valid: Object.keys(errors).length === 0, errors };
}

/** Prescription save — a prescription needs a patient, a date and some content. */
export function validatePrescription({ patientName, date, medicines, labTests, xrays, diagnosis }) {
  const errors = {};
  if (!required(patientName)) errors.patientName = MESSAGES.required;
  if (!required(date)) errors.date = MESSAGES.dateRequired;

  const hasContent =
    (Array.isArray(medicines) && medicines.some((m) => required(m?.name))) ||
    (Array.isArray(labTests) && labTests.some((t) => required(t?.name))) ||
    (Array.isArray(xrays) && xrays.some((x) => required(x?.name))) ||
    required(diagnosis);

  if (!hasContent) errors.content = 'يجب إضافة تشخيص أو دواء أو تحليل واحد على الأقل';
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validatePackage({ name, type, price, quantity }) {
  const errors = {};
  if (!required(name)) errors.name = MESSAGES.required;
  if (!required(type)) errors.type = MESSAGES.required;
  if (!isPositiveNumber(price)) errors.price = MESSAGES.priceInvalid;
  if (quantity !== undefined && !isPositiveNumber(quantity)) errors.quantity = MESSAGES.priceInvalid;
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateTeamMember({ name, username, password, isNew = true }) {
  const errors = {};
  if (!required(name)) errors.name = MESSAGES.required;
  if (!required(username)) errors.username = MESSAGES.required;
  if (isNew) {
    if (!required(password)) errors.password = MESSAGES.required;
    else if (String(password).length < 6) errors.password = MESSAGES.passwordShort;
  } else if (required(password) && String(password).length < 6) {
    errors.password = MESSAGES.passwordShort;
  }
  return { valid: Object.keys(errors).length === 0, errors };
}
